import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mailApi, parseAddresses, type Communication } from "../../api/mail";
import { adminApi, type CompanyContact } from "../../api/admin";
import { offersApi, type Offer } from "../../api/offers";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { localeFlag, localeLabel } from "../../i18n/LocaleSwitcher";
import { useToast } from "../Toast";
import { Select } from "../Select";
import Ic from "../icons.jsx";
import { OFFER_MAIL_LANGUAGES, firstName, offerMail, type OfferMailLanguage } from "./offerMailTemplate";

/**
 * "Pošalji mejl" — composes one message through the caller's OWN connected
 * mailbox (POST /api/mail/send) and hands the persisted timeline row back so
 * the caller can refresh its CommunicationTimeline.
 *
 * The message always hangs off a lead or a customer company (backend
 * MAIL_TARGET_REQUIRED), so exactly one of `leadId` / `customerCompanyId` must
 * be supplied. When the caller has no working connection the backend answers
 * 409 MAIL_NOT_CONNECTED — that is surfaced as an explicit banner pointing at
 * Settings → "Moj mejl" rather than a generic toast, because it is a setup
 * problem the user has to go and fix.
 */
/** One row of the offer picker — the full Offer is never needed here. */
type OfferOption = { id: string; offerNumber: string; customerName?: string | null };

/** Deliberately loose: the mail server is the authority, this only catches typos. */
function looksLikeEmail(address: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);
}

export type SendMailModalProps = {
  open: boolean;
  onClose: () => void;
  /** Called after a successful send with the persisted communication row. */
  onSent?: (sent: Communication) => void;
  leadId?: string | null;
  customerCompanyId?: string | null;
  /** Prefilled recipient (lead email). Wins over the company-contact lookup. */
  defaultTo?: string | null;
  defaultSubject?: string | null;
  /** Offer preselected as the attachment (offer editor "Pošalji kupcu"). */
  offer?: Pick<Offer, "id" | "offerNumber"> | null;
  /** When set, the offer picker lists only this company's offers. */
  offerCompanyId?: string | null;
};

export function SendMailModal({
  open,
  onClose,
  onSent,
  leadId,
  customerCompanyId,
  defaultTo,
  defaultSubject,
  offer,
  offerCompanyId,
}: SendMailModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { state: auth } = useAuth();
  const senderName =
    auth.status === "authenticated" ? `${auth.user.firstName} ${auth.user.lastName}`.trim() || null : null;

  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachOffer, setAttachOffer] = useState(false);
  const [offerId, setOfferId] = useState<string>("");
  const [offers, setOffers] = useState<OfferOption[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [notConnected, setNotConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The ready-made offer mail: filled from the attached offer in the chosen language until the
  // user edits subject or text by hand; choosing another language writes it anew.
  const [contacts, setContacts] = useState<CompanyContact[]>([]);
  const [offerDetails, setOfferDetails] = useState<Offer | null>(null);
  const [mailLanguage, setMailLanguage] = useState<OfferMailLanguage>("SR");
  const [edited, setEdited] = useState(false);

  // Reset the whole form every time the modal opens, so a previous draft (or a
  // previous failure banner) never leaks into the next message.
  //
  // The effect deliberately depends on the preselected offer's FIELDS, not on
  // the `offer` object: callers build it inline (`offer={{ id, offerNumber }}`),
  // so a new identity arrives on every parent render and depending on the
  // object would wipe a half-typed message the moment the parent re-rendered.
  const pinnedOfferId = offer?.id ?? null;
  const pinnedOfferNumber = offer?.offerNumber ?? null;
  useEffect(() => {
    if (!open) return;
    setTo(defaultTo ?? "");
    setCc("");
    setSubject(defaultSubject ?? "");
    setBody("");
    setAttachOffer(pinnedOfferId != null);
    setOfferId(pinnedOfferId ?? "");
    setOffers(
      pinnedOfferId != null ? [{ id: pinnedOfferId, offerNumber: pinnedOfferNumber ?? pinnedOfferId }] : null,
    );
    setBusy(false);
    setNotConnected(false);
    setError(null);
    setContacts([]);
    setOfferDetails(null);
    setEdited(false);
  }, [open, defaultTo, defaultSubject, pinnedOfferId, pinnedOfferNumber]);

  // No lead email / no explicit prefill → fall back to the company's primary
  // contact. Best-effort: a caller without companies:view simply types the
  // address by hand.
  // The contacts also give the greeting its name, so they are kept even when the address was prefilled.
  useEffect(() => {
    if (!open || !customerCompanyId) return;
    let live = true;
    void adminApi
      .listCompanyContacts(customerCompanyId)
      .then((contacts) => {
        if (!live) return;
        setContacts(contacts);
        if (defaultTo) return;
        const withEmail = contacts.filter((c) => c.email);
        const pick = withEmail.find((c) => c.primary) ?? withEmail[0];
        if (pick?.email) setTo((prev) => (prev ? prev : pick.email!));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [open, defaultTo, customerCompanyId]);

  // The attached offer in full: its lines, its end date and the language its document is made in.
  const templateOfferId = open && attachOffer && offerId ? offerId : null;
  useEffect(() => {
    if (!templateOfferId) {
      setOfferDetails(null);
      return;
    }
    let live = true;
    void offersApi
      .get(templateOfferId)
      .then((o) => {
        if (!live) return;
        setOfferDetails(o);
        setMailLanguage(o.documentLanguage === "EN" ? "EN" : "SR");
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [templateOfferId]);

  // The greeting names the contact the first address belongs to; an unknown address is greeted without a name.
  const firstRecipient = parseAddresses(to)[0]?.toLowerCase() ?? "";
  const recipientName = firstName(contacts.find((c) => c.email && c.email.toLowerCase() === firstRecipient)?.name);
  useEffect(() => {
    if (!offerDetails || edited) return;
    const mail = offerMail(mailLanguage, {
      offerNumber: offerDetails.offerNumber,
      items: offerDetails.items.map((i) => i.name),
      validUntil: offerDetails.validUntil,
      recipientName,
      senderName,
    });
    setSubject(mail.subject);
    setBody(mail.body);
  }, [offerDetails, mailLanguage, recipientName, senderName, edited]);

  // Offers are only fetched once the user actually asks to attach one.
  useEffect(() => {
    if (!open || !attachOffer || offers !== null) return;
    let live = true;
    // Prilog uz mejl bira ponudu te firme; filter ide u upit, ne posle učitavanja.
    void offersApi
      .list({ companyId: offerCompanyId || undefined, size: 200 })
      .then((r) => {
        if (!live) return;
        setOffers(r.content.map((o) => ({ id: o.id, offerNumber: o.offerNumber, customerName: o.customerName })));
      })
      .catch(() => {
        if (live) setOffers([]);
      });
    return () => {
      live = false;
    };
  }, [open, attachOffer, offers, offerCompanyId]);

  if (!open) return null;

  const recipients = parseAddresses(to);
  const canSubmit = recipients.length > 0 && subject.trim() !== "" && body.trim() !== "" && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    // The backend rejects a malformed address with a bean-validation 400 whose
    // message is not worth showing; catch it here with a localized one instead.
    const ccList = parseAddresses(cc);
    if (![...recipients, ...ccList].every(looksLikeEmail)) {
      setError(t("mail.send.invalidAddress", "Check the recipient addresses."));
      return;
    }
    setBusy(true);
    setError(null);
    setNotConnected(false);
    try {
      const sent = await mailApi.send({
        leadId: leadId ?? null,
        customerCompanyId: customerCompanyId ?? null,
        to: recipients,
        cc: ccList,
        subject: subject.trim(),
        bodyText: body.trim(),
        offerId: attachOffer && offerId ? offerId : null,
      });
      toast.success(t("mail.send.sent", "Message sent."));
      onSent?.(sent);
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.code === "MAIL_NOT_CONNECTED") {
        setNotConnected(true);
      } else {
        setError(apiErr.message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      style={overlay}
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="modal-panel"
        style={panel}
        role="dialog"
        aria-modal="true"
        aria-label={t("mail.send.title", "Send email")}
        onClick={(e) => e.stopPropagation()}
      >
        <header style={head}>
          <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>{t("mail.send.title", "Send email")}</h3>
          <button type="button" className="btn ghost sm" onClick={onClose} disabled={busy} aria-label={t("mail.action.close", "Close")}>
            <Ic.x />
          </button>
        </header>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {notConnected && (
            <div style={warnBox}>
              <strong>{t("mail.send.notConnectedTitle", "No working mailbox")}</strong>
              <div style={{ marginTop: 4 }}>
                {t(
                  "mail.send.notConnectedBody",
                  "Connect and test your mailbox before sending. Settings → My email.",
                )}
              </div>
              <Link to="/settings/mail" className="btn sm" style={{ textDecoration: "none", marginTop: 8, display: "inline-flex" }}>
                <Ic.cog /> {t("mail.nav.settings", "My email")}
              </Link>
            </div>
          )}
          {error && <div style={errBox}>{error}</div>}

          <Field label={t("mail.send.to", "To")} required>
            <input
              className="input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={t("mail.send.toPlaceholder", "name@company.com, other@company.com")}
              autoFocus
            />
          </Field>

          <Field label={t("mail.send.cc", "Cc")}>
            <input
              className="input"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder={t("mail.send.ccPlaceholder", "Optional")}
            />
          </Field>

          {offerDetails && (
            <Field label={t("mail.send.language", "Mail language")}>
              <Select<OfferMailLanguage>
                value={mailLanguage}
                onChange={(language) => {
                  setMailLanguage(language);
                  setEdited(false);
                }}
                ariaLabel={t("mail.send.language", "Mail language")}
                options={OFFER_MAIL_LANGUAGES.map((l) => {
                  const locale = l === "SR" ? "sr" : "en";
                  return {
                    value: l,
                    label: localeLabel(locale),
                    left: <span style={{ fontSize: 14, lineHeight: 1 }}>{localeFlag(locale)}</span>,
                  };
                })}
              />
            </Field>
          )}

          <Field label={t("mail.send.subject", "Subject")} required>
            <input
              className="input"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setEdited(true);
              }}
              maxLength={500}
            />
          </Field>

          <Field label={t("mail.send.body", "Message")} required>
            <textarea
              className="input"
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setEdited(true);
              }}
              rows={9}
              style={{ resize: "vertical" }}
            />
          </Field>

          <label style={checkRow}>
            <input type="checkbox" checked={attachOffer} onChange={(e) => setAttachOffer(e.target.checked)} />
            <span>{t("mail.send.attachOffer", "Attach an offer")}</span>
          </label>

          {attachOffer && (
            <Field label={t("mail.send.offer", "Offer")}>
              {offers === null ? (
                <span style={{ fontSize: 13, color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</span>
              ) : offers.length === 0 ? (
                <span style={{ fontSize: 13, color: "var(--ink-3)" }}>{t("mail.send.noOffers", "No offers available.")}</span>
              ) : (
                <Select
                  value={offerId}
                  onChange={setOfferId}
                  ariaLabel={t("mail.send.offerNone", "— Choose an offer —")}
                  options={[
                    { value: "", label: t("mail.send.offerNone", "— Choose an offer —") },
                    ...offers.map((o) => ({
                      value: o.id,
                      label: o.customerName ? `${o.offerNumber} · ${o.customerName}` : o.offerNumber,
                    })),
                  ]}
                />
              )}
              <span style={hint}>{t("mail.send.offerHint", "The offer PDF is generated and attached automatically.")}</span>
            </Field>
          )}

          <div style={actions}>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              {t("action.cancel", "Cancel")}
            </button>
            <button type="submit" className="btn primary" disabled={!canSubmit}>
              <Ic.send /> {busy ? t("mail.send.sending", "Sending…") : t("mail.send.submit", "Send")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={{ color: "var(--ink-3)" }}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--overlay)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 60,
  padding: 20,
};

const panel: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  padding: 20,
  width: 620,
  maxWidth: "100%",
  maxHeight: "90dvh",
  overflowY: "auto",
  boxShadow: "var(--shadow-2)",
};

const head: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 };
const actions: CSSProperties = { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 };
const checkRow: CSSProperties = { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink-2)", cursor: "pointer" };
const hint: CSSProperties = { fontSize: 12, color: "var(--ink-3)" };
const warnBox: CSSProperties = { padding: 12, borderRadius: 8, background: "var(--warn-bg)", border: "1px solid var(--warn)", color: "var(--ink)", fontSize: 13 };
const errBox: CSSProperties = { padding: 12, borderRadius: 8, background: "var(--err-bg)", color: "var(--err)", fontSize: 13 };
